import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  CalendarIcon,
  ClockIcon,
  UserIcon,
  TagIcon,
  ArrowLeftIcon,
  QrCodeIcon,
  MagnifyingGlassIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline'

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')

  const categories = [
    { id: 'all', name: 'All Posts', count: 24 },
    { id: 'industry-insights', name: 'Industry Insights', count: 8 },
    { id: 'product-updates', name: 'Product Updates', count: 6 },
    { id: 'success-stories', name: 'Success Stories', count: 5 },
    { id: 'tips-tricks', name: 'Tips & Tricks', count: 5 }
  ]

  const featuredPost = {
    id: 1,
    title: 'The Future of Restaurant Technology: QR Codes and Beyond',
    excerpt: 'Explore how QR code technology is revolutionizing the restaurant industry and what innovations lie ahead for digital dining experiences.',
    content: 'The restaurant industry has undergone a massive transformation in recent years...',
    author: 'Ahammed S',
    date: '2024-01-15',
    readTime: '8 min read',
    category: 'Industry Insights',
    image: '/blog/future-restaurant-tech.jpg',
    featured: true
  }

  const blogPosts = [
    {
      id: 2,
      title: 'How to Increase Restaurant Revenue with QR Code Ordering',
      excerpt: 'Learn proven strategies to boost your restaurant sales using QR code ordering systems and digital menu optimization.',
      author: 'Sarah Johnson',
      date: '2024-01-12',
      readTime: '6 min read',
      category: 'Tips & Tricks',
      image: '/blog/increase-revenue.jpg'
    },
    {
      id: 3,
      title: 'Customer Success Story: How Spice Garden Increased Orders by 40%',
      excerpt: 'Discover how Spice Garden restaurant transformed their operations and significantly boosted customer satisfaction.',
      author: 'Raj Patel',
      date: '2024-01-10',
      readTime: '5 min read',
      category: 'Success Stories',
      image: '/blog/spice-garden-success.jpg'
    },
    {
      id: 4,
      title: 'New Feature: Advanced Analytics Dashboard',
      excerpt: 'Introducing our new analytics dashboard with real-time insights, custom reports, and performance metrics.',
      author: 'Lisa Chen',
      date: '2024-01-08',
      readTime: '4 min read',
      category: 'Product Updates',
      image: '/blog/analytics-dashboard.jpg'
    },
    {
      id: 5,
      title: 'Best Practices for QR Code Menu Design',
      excerpt: 'Essential design principles to create engaging and user-friendly QR code menus that customers love.',
      author: 'Design Team',
      date: '2024-01-05',
      readTime: '7 min read',
      category: 'Tips & Tricks',
      image: '/blog/menu-design.jpg'
    },
    {
      id: 6,
      title: 'The Rise of Contactless Dining: Industry Report 2024',
      excerpt: 'Comprehensive analysis of contactless dining trends, customer preferences, and market predictions.',
      author: 'Research Team',
      date: '2024-01-03',
      readTime: '10 min read',
      category: 'Industry Insights',
      image: '/blog/contactless-dining.jpg'
    },
    {
      id: 7,
      title: 'Integration Spotlight: Cashfree Payment Gateway',
      excerpt: 'Learn how our Cashfree integration enables seamless split payments and enhances customer experience.',
      author: 'Tech Team',
      date: '2024-01-01',
      readTime: '5 min read',
      category: 'Product Updates',
      image: '/blog/cashfree-integration.jpg'
    }
  ]

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category.toLowerCase().replace(/\s+/g, '-') === selectedCategory
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase())
    return matchesCategory && matchesSearch
  })

  const getCategoryColor = (category) => {
    const colors = {
      'Industry Insights': 'bg-white border-4 border-black text-black font-black shadow-[2px_2px_0_0_rgba(0,0,0,1)]',
      'Product Updates': 'bg-white border-4 border-black text-black font-black shadow-[2px_2px_0_0_rgba(0,0,0,1)]',
      'Success Stories': 'bg-white border-4 border-black text-black font-black shadow-[2px_2px_0_0_rgba(0,0,0,1)]',
      'Tips & Tricks': 'bg-amber-500 border-4 border-black text-black font-black shadow-[2px_2px_0_0_rgba(0,0,0,1)]'
    }
    return colors[category] || 'bg-white border-4 border-black text-black font-black shadow-[2px_2px_0_0_rgba(0,0,0,1)]'
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Matching LandingPage Design */}
      <div className="bg-white shadow-[4px_4px_0_0_rgba(0,0,0,1)] border-4 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/" 
              className="flex items-center space-x-2 bg-white border-4 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] px-4 py-2 rounded-lg hover:shadow-[2px_2px_0_0_rgba(0,0,0,1)] transition-all duration-200"
            >
              <ArrowLeftIcon className="h-5 w-5 text-black" />
              <span className="font-black text-black tracking-tighter">Back to Home</span>
            </Link>
            <div className="flex items-center space-x-3 bg-amber-500 border-4 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] px-6 py-3 rounded-lg">
              <QrCodeIcon className="h-8 w-8 text-black" />
              <span className="text-2xl font-black text-black tracking-tighter">QR Restaurant Blog</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section - Matching LandingPage Design */}
      <section className="py-16 sm:py-24 bg-amber-500 border-b-4 border-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl sm:text-6xl font-black text-black tracking-tighter mb-6">
              Restaurant Technology
              <span className="block text-black"> Insights</span>
            </h1>
            <p className="text-xl font-bold text-black mb-8 tracking-tight">
              Stay updated with the latest trends, tips, and insights in restaurant technology and digital dining experiences.
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-2xl mx-auto">
              <MagnifyingGlassIcon className="absolute left-6 top-1/2 transform -translate-y-1/2 h-6 w-6 text-black" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-16 pr-6 py-4 border-4 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] rounded-lg focus:shadow-[2px_2px_0_0_rgba(0,0,0,1)] transition-all duration-200 text-lg font-bold bg-white text-black placeholder-gray-600"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories - Matching LandingPage Design */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-lg font-black tracking-tighter transition-all duration-200 border-4 border-black ${
                  selectedCategory === category.id
                    ? 'bg-amber-500 text-black shadow-[4px_4px_0_0_rgba(0,0,0,1)]'
                    : 'bg-white text-black hover:bg-amber-100 shadow-[2px_2px_0_0_rgba(0,0,0,1)] hover:shadow-[4px_4px_0_0_rgba(0,0,0,1)]'
                }`}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white border-4 border-black shadow-[4px_4px_0_0_rgba(0,0,0,1)] rounded-lg overflow-hidden"
          >
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="aspect-video lg:aspect-auto bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center">
                <div className="text-white text-center p-8">
                  <QrCodeIcon className="h-24 w-24 mx-auto mb-4 opacity-75" />
                  <p className="text-lg font-medium">Featured Article</p>
                </div>
              </div>
              <div className="p-8">
                <div className="flex items-center space-x-4 mb-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(featuredPost.category)}`}>
                    {featuredPost.category}
                  </span>
                  <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
                    Featured
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{featuredPost.title}</h2>
                <p className="text-gray-600 mb-6">{featuredPost.excerpt}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <UserIcon className="h-4 w-4 mr-1" />
                      {featuredPost.author}
                    </div>
                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {new Date(featuredPost.date).toLocaleDateString()}
                    </div>
                    <div className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {featuredPost.readTime}
                    </div>
                  </div>
                  <button className="flex items-center text-orange-600 hover:text-orange-700 font-medium">
                    Read More
                    <ArrowRightIcon className="h-4 w-4 ml-1" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group"
              >
                <div className="aspect-video bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                  <div className="text-gray-500 text-center">
                    <QrCodeIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Article Image</p>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(post.category)}`}>
                      {post.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center">
                        <UserIcon className="h-4 w-4 mr-1" />
                        {post.author}
                      </div>
                      <div className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        {post.readTime}
                      </div>
                    </div>
                    <div className="flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {new Date(post.date).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-8 sm:p-12 text-white"
          >
            <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
            <p className="text-xl mb-8 opacity-90">
              Subscribe to our newsletter for the latest restaurant technology insights and product updates.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
              />
              <button className="bg-white text-orange-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors">
                Subscribe
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Popular Tags */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Popular Topics</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'QR Code Technology',
              'Restaurant Management',
              'Digital Menus',
              'Payment Integration',
              'Customer Experience',
              'Analytics',
              'Mobile Ordering',
              'Contactless Dining',
              'Restaurant Growth',
              'Technology Trends'
            ].map((tag, index) => (
              <motion.span
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm hover:bg-gray-200 transition-colors cursor-pointer"
              >
                #{tag.replace(/\s+/g, '')}
              </motion.span>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}

export default Blog
